"use client"

import { useState } from "react"
import { UserPlus, GraduationCap, Award, ShieldCheck, DollarSign, Bell, ChevronUp } from "lucide-react"
import { cn } from "../../lib/utils"

const TYPE_ICONS = {
  enrollment: UserPlus,
  completion: GraduationCap,
  credential_issued: Award,
  credential_verified: ShieldCheck,
  purchase: DollarSign,
}

// "3m ago", "2h ago", "Yesterday", or a short date — computed from a real
// millisecond timestamp, not a hardcoded string.
const relativeTime = (ms) => {
  if (!ms) return ""
  const diff = Date.now() - ms
  const minute = 60000
  const hour = 60 * minute
  const day = 24 * hour

  if (diff < minute) return "Just now"
  if (diff < hour) return `${Math.floor(diff / minute)}m ago`
  if (diff < day) return `${Math.floor(diff / hour)}h ago`
  if (diff < 2 * day) return "Yesterday"
  return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

export function ActivityDropdown({ activities = [], loading = false }) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div
      className={cn(
        "w-full max-w-md rounded-2xl shadow-2xl overflow-hidden cursor-pointer select-none",
        "bg-white dark:bg-neutral-900",
        "shadow-xl shadow-black/10 dark:shadow-black/50",
        "transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]",
        isOpen ? "rounded-3xl" : "rounded-2xl"
      )}
      onClick={() => setIsOpen(!isOpen)}
    >
      {/* Header */}
      <div className="flex items-center gap-4 p-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-neutral-100 dark:bg-neutral-800 transition-colors duration-300">
          <Bell className="h-5 w-5 text-neutral-600 dark:text-neutral-300" />
        </div>
        <div className="flex-1 overflow-hidden">
          <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
            {loading ? "Loading activity..." : `${activities?.length || 0} Recent Activities`}
          </h3>
          <p
            className={cn(
              "text-sm text-neutral-500 dark:text-neutral-400",
              "transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]",
              isOpen ? "opacity-0 max-h-0 mt-0" : "opacity-100 max-h-6 mt-0.5"
            )}
          >
            What's happening across your organization
          </p>
        </div>
        <div className="flex h-8 w-8 items-center justify-center">
          <ChevronUp
            className={cn(
              "h-5 w-5 text-neutral-400 transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]",
              isOpen ? "rotate-0" : "rotate-180"
            )}
          />
        </div>
      </div>

      {/* Activity List */}
      <div
        className={cn(
          "grid",
          "transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]",
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        )}
      >
        <div className="overflow-hidden">
          <div className="px-2 pb-4">
            {activities?.length === 0 ? (
              <p className="text-sm text-neutral-400 dark:text-neutral-500 px-3 py-4 text-center">
                No activity yet — this fills in as learners enroll, complete courses, and earn credentials.
              </p>
            ) : (
              <div className="space-y-1">
                {activities?.map((activity, index) => {
                  const Icon = TYPE_ICONS?.[activity?.type] || Bell
                  return (
                    <div
                      key={`${activity?.type}-${activity?.ts}-${index}`}
                      className={cn(
                        "flex items-start gap-3 rounded-xl p-3",
                        "transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]",
                        "hover:bg-neutral-100 dark:hover:bg-neutral-800/50",
                        isOpen ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                      )}
                      style={{ transitionDelay: isOpen ? `${index * 60}ms` : "0ms" }}
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-neutral-100 dark:bg-neutral-700 transition-colors duration-300">
                        <span className="text-neutral-600 dark:text-neutral-300">
                          <Icon className="h-4 w-4" />
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">{activity?.title}</p>
                      </div>
                      <span className="text-xs text-neutral-400 dark:text-neutral-500 shrink-0 pt-0.5">
                        {relativeTime(activity?.ts)}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ActivityDropdown